/**
 * useTaxonomy — Hook léger pour la taxonomie des propriétés.
 *
 * Stratégie :
 *  1. Premier render synchrone : valeur en mémoire / localStorage / fallback JSON local
 *  2. Au mount + sur focus + toutes les 60 s : fetch `/api/taxonomy/properties` (avec ETag)
 *  3. Si la `version` retournée est plus récente que la version cachée → swap + persist
 *  4. ETag → 304 quand rien n'a changé, coût quasi nul
 *
 * Le fallback hardcodé garantit que l'UI n'est jamais vide même hors-ligne.
 * Admin peut propager une nouvelle taxonomie via POST /api/admin/taxonomy/refresh
 * → tous les onglets actifs voient le changement en moins d'1 minute.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import fallback from '../data/property_taxonomy.json';

const STORAGE_KEY = 'realty237.taxonomy.v1';
const POLL_INTERVAL_MS = 60_000;
let _memoryCache = null; // shared across all hook instances

const readStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.types || !parsed?.purposes) return null;
    return parsed;
  } catch {
    return null;
  }
};

const initial = () => _memoryCache || readStorage() || { ...fallback, version: 0 };

const fetchTaxonomy = async (signal) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/taxonomy/properties`;
  const headers = {};
  const current = _memoryCache || readStorage();
  if (current?.etag) headers['If-None-Match'] = current.etag;
  const r = await axios.get(url, { timeout: 6000, signal, headers, validateStatus: (s) => s === 200 || s === 304 });
  if (r.status === 304) return null; // unchanged
  const etag = r.headers?.etag || r.headers?.ETag || null;
  if (!r.data?.types || !r.data?.purposes) return null;
  return {
    version: r.data.version || 0,
    types: r.data.types,
    purposes: r.data.purposes,
    etag,
  };
};

export const useTaxonomy = () => {
  const [taxonomy, setTaxonomy] = useState(initial);
  const [loading, setLoading] = useState(!_memoryCache);

  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();

    const refresh = async () => {
      try {
        const next = await fetchTaxonomy(ctrl.signal);
        if (!alive || !next) return;
        const cur = _memoryCache || readStorage();
        if (!cur || cur.version !== next.version
              || JSON.stringify(cur.types) !== JSON.stringify(next.types)
              || JSON.stringify(cur.purposes) !== JSON.stringify(next.purposes)) {
          _memoryCache = next;
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* quota */ }
          setTaxonomy(next);
        } else {
          _memoryCache = next; // keep latest etag
        }
      } catch {
        /* keep fallback / cached value */
      } finally {
        if (alive) setLoading(false);
      }
    };

    refresh();
    const onFocus = () => refresh();
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);

    return () => {
      alive = false;
      ctrl.abort();
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return {
    types: taxonomy.types,
    purposes: taxonomy.purposes,
    version: taxonomy.version || 0,
    loading,
  };
};

export default useTaxonomy;
